/**
 * Catalog instance-count honesty.
 *
 * Model cards painted Total=1 / Online=0 / Offline=0 while the devices
 * list and fleet-summary were 0. PIM / derived model stubs are not
 * devices. Unknown-status leftovers were also hidden because the card
 * omitted Unknown, so 1 ≠ 0+0.
 *
 * Online + Offline + Unknown must equal Total. A claimed total with no
 * presence buckets, or any claimed devices when the fleet is empty, is
 * a template stub — label it as a model, not a device count.
 */

export type CatalogCountKind = 'devices' | 'template';

export type CatalogInstanceCounts = {
  total: number;
  online: number;
  offline: number;
  unknown: number;
};

export type CatalogCountDisplay = CatalogInstanceCounts & {
  kind: CatalogCountKind;
};

function toCount(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

export function catalogCountsAreClosed(counts: CatalogInstanceCounts): boolean {
  return counts.online + counts.offline + counts.unknown === counts.total;
}

/**
 * Normalize a model's `instances` payload for display.
 *
 * - Close the identity: remainder of Total − (Online+Offline+Unknown) is Unknown.
 * - Fleet empty, or a claimed Total with every presence bucket at 0, is a
 *   template stub (0 devices). Never paint Total=1 Online=0 Offline=0.
 */
export function normalizeCatalogInstanceCounts(
  raw: Partial<CatalogInstanceCounts> | null | undefined,
  opts?: { source?: string | null; fleetDeviceTotal?: number | null }
): CatalogCountDisplay {
  const online = toCount(raw?.online);
  const offline = toCount(raw?.offline);
  const unknownRaw = toCount(raw?.unknown);
  const claimedTotal = toCount(raw?.total);
  const present = online + offline + unknownRaw;
  const fleetEmpty = opts?.fleetDeviceTotal === 0;
  const inventedTotal = claimedTotal > 0 && present === 0;
  const noDevices = claimedTotal === 0 && present === 0;

  if (fleetEmpty || inventedTotal || noDevices) {
    return {
      kind: 'template',
      total: 0,
      online: 0,
      offline: 0,
      unknown: 0,
    };
  }

  const unknown = unknownRaw + Math.max(0, claimedTotal - present);
  const total = Math.max(claimedTotal, online + offline + unknown);

  return {
    kind: 'devices',
    total,
    online,
    offline,
    unknown,
  };
}

export function catalogCountHeadline(display: CatalogCountDisplay): string {
  if (display.kind === 'template') {
    return 'Template — 0 devices';
  }
  return `${display.total} total • ${display.online} online • ${display.offline} offline • ${display.unknown} unknown`;
}
